import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';

import { differenceInYears, format, parseISO, startOfToday } from 'date-fns';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { useLocation } from 'react-router-dom';
import Loading from '../../components/Loading';
import { useToast } from '../../hooks/toast';
import SelectSync, { IOption } from '../../components/SelectSync';
import api from '../../services/api';
import getResponseErrors from '../../utils/getResponseErrors';
import { IFilaChamado, IListaAgendamentosHojeDTO } from '../AgendaAtendimento';
import {
  STATUS_AGENDA,
  formataIdade,
  formataTelefone,
  statusAgendamento,
} from '../../utils/funcoes';
import ListaAtendimentoConsulta from '../../components/ListaAtendimentoConsulta';
import { useAgenda } from '../../hooks/agenda';
import { IAgendaSala } from '../AgendaSalas';
import socket from '../../services/socket';

const ListaEsperaAtendimento: React.FC = () => {
  const { addToast } = useToast();
  const { retornaDadosAgenda } = useAgenda();
  const [loading, setLoading] = useState(false);
  const [agendaId, setAgendaId] = useState<string>();
  const [optionsAgenda, setOptionsAgenda] = useState<IOption[]>([]);
  const [listaEspera, setListaEspera] = useState<IListaAgendamentosHojeDTO[]>(
    [],
  );
  const [atualiza, setAtualiza] = useState<string | undefined>(undefined);
  const [nomeSala, setNomeSala] = useState('');

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const formRefBusca = useRef<FormHandles>(null);

  const agenda = query.get('agenda');

  useEffect(() => {
    const handleNewAtendimento = (codAgenda: number): void => {
      if (agendaId && codAgenda === Number(agendaId))
        setAtualiza(format(new Date(), 'yyyy-MM-dd HH:mm:ss'));
    };

    socket.on('atendimento', handleNewAtendimento);
    return () => {
      socket.removeEventListener('atendimento', handleNewAtendimento);
    };
  }, [agendaId]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      const options = await retornaDadosAgenda();
      setOptionsAgenda(options);
    }
    loadDados();
  }, [retornaDadosAgenda]);

  useEffect(() => {
    if (optionsAgenda.length > 0 && agenda && agenda?.length > 1) {
      formRefBusca.current?.setFieldValue('agenda', agenda);
      setAgendaId(agenda || undefined);
    }
  }, [agenda, optionsAgenda.length]);

  useEffect(() => {
    async function BuscaDados(): Promise<void> {
      let sala = '';
      try {
        setLoading(true);
        setNomeSala(sala);
        const responseSala = await api.get<IAgendaSala>(`/salas/${agendaId}`);

        sala = responseSala.data.NomeSala;
        setNomeSala(sala);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
      setLoading(true);
      setListaEspera([]);
      try {
        const dataAtualFormatada = format(startOfToday(), 'yyyy-MM-dd');
        const response = await api.get<IListaAgendamentosHojeDTO[]>(
          `atendimentos/emespera?ConCodigo=${agendaId}&Data=${dataAtualFormatada}&AgeCompleta=false`,
        );
        if (response.data.length === 0) setListaEspera([]);

        const retornoFormatado = response.data.map(lista => ({
          ...lista,
          idade: formataIdade(parseISO(lista.PacDataNasc.toString())),
          idadeNumero: differenceInYears(
            new Date(),
            parseISO(lista.PacDataNasc.toString()),
          ),
          horario: format(parseISO(lista.AgeInicio.toString()), 'HH:mm'),
          liberado: lista.AgeDataLiberacao
            ? format(parseISO(lista.AgeDataLiberacao.toString()), 'HH:mm')
            : '',
          celular: formataTelefone(lista.PacCelular),
          telefone: formataTelefone(lista.PacTelefone),
          icone: statusAgendamento.find(
            status => status.id === Number(lista.AgeStatus),
          ),
          atendido: lista.AgeStatus === STATUS_AGENDA.ATENDIDO,
        }));
        setListaEspera(retornoFormatado);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar lista de espera',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (agendaId) BuscaDados();
  }, [addToast, agendaId, atualiza]);

  const handleChamarPacientePainel = useCallback(
    async (dados: IFilaChamado) => {
      try {
        setLoading(true);

        await api.post<IFilaChamado>(`/filaChamados`, dados);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return (
    <Container fluid className="mt-2">
      {loading && <Loading backgroundBlack={false} />}

      <div className="bg-white border p-2">
        <h2>Lista de Espera para Atendimento</h2>

        <Form ref={formRefBusca} onSubmit={dados => console.log(dados)}>
          <SelectSync
            name="agenda"
            label="Agendas"
            options={optionsAgenda}
            isClearable
            handleSelectChange={dados => setAgendaId(dados.value)}
          />
        </Form>

        {listaEspera.length > 0 ? (
          <div className="mt-4">
            <ListaAtendimentoConsulta
              dadosBusca={{
                codigoAgenda: '1',
                dataAgenda: '2024-01-01',
                somenteLista: true,
              }}
              listaEspera={listaEspera}
              exibeBotoesAcao={false}
              chamar={() => {
                console.log('ok');
              }}
              chamarPainel={handleChamarPacientePainel}
              abrirHistorico={() => {
                console.log('ok');
              }}
              sala={nomeSala}
            />
          </div>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>Nenhum paciente na lista de espera</h5>
          </div>
        )}
      </div>
    </Container>
  );
};

export default ListaEsperaAtendimento;
